import React, { FC, useMemo } from 'react';
import { Avatar, type AvatarProps, type SxProps, type Theme } from '@mui/material';
import Box from '@mui/material/Box';
import { mergeSx } from 'merge-sx';

import { useAvatarDisplay } from '@/hooks/useAvatarDisplay';
import { BasicUser, getUserAvatarColor, getUserInitials } from '@/utils/user';

type UserAvatarSize = 'xsmall' | 'small' | 'medium' | 'large' | number;

interface UserAvatarProps {
  user?: BasicUser | null;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  avatarUrl?: string;
  size?: UserAvatarSize;
  variant?: AvatarProps['variant'];
  sx?: SxProps<Theme>;
  alt?: string;
  /** Force initials regardless of global showImages setting */
  forceInitials?: boolean;
  /** Force image regardless of global showImages setting */
  forceImage?: boolean;
}

const SIZE_MAP: Record<string, { width: number; height: number; fontSize: string }> = {
  xsmall: { width: 20, height: 20, fontSize: '0.625rem' },
  small: { width: 24, height: 24, fontSize: '0.75rem' },
  medium: { width: 32, height: 32, fontSize: '0.875rem' },
  large: { width: 96, height: 96, fontSize: '3rem' }
};

const SIZE_TO_VARIANT: Record<string, AvatarProps['variant']> = {
  xsmall: 'user-small',
  small: undefined,
  medium: 'talent-xsmall',
  large: 'talent-large'
};

// eslint-disable-next-line react-refresh/only-export-components
export const getUserAvatarFontSize = (size: number): string => `${Math.max(size * 0.4, 10)}px`;

const DROPDOWN_AVATAR_VALUE_SIZE = 20;
const DROPDOWN_AVATAR_MENU_SIZE = 32;

interface UserAvatarForDropdownProps {
  user?: BasicUser | null;
  sx?: SxProps<Theme>;
}

export const UserAvatarForDropdown: FC<UserAvatarForDropdownProps> = ({ user, sx }) => (
  <Box
    sx={{
      '[data-context="menu"] & .MuiAvatar-root': {
        width: DROPDOWN_AVATAR_MENU_SIZE,
        height: DROPDOWN_AVATAR_MENU_SIZE,
        fontSize: getUserAvatarFontSize(DROPDOWN_AVATAR_MENU_SIZE)
      },
      '[data-context="value"] & .MuiAvatar-root': {
        width: DROPDOWN_AVATAR_VALUE_SIZE,
        height: DROPDOWN_AVATAR_VALUE_SIZE,
        fontSize: getUserAvatarFontSize(DROPDOWN_AVATAR_VALUE_SIZE)
      }
    }}
  >
    <UserAvatar user={user} sx={mergeSx({ ml: 0.5, mr: 0.5 }, sx)} />
  </Box>
);

export const UserAvatar: FC<UserAvatarProps> = ({
  user,
  firstName,
  lastName,
  fullName,
  avatarUrl,
  size = 'medium',
  variant,
  sx,
  alt,
  forceInitials,
  forceImage
}) => {
  const { showImages: globalShowImages } = useAvatarDisplay();

  const userObj = useMemo<BasicUser>(
    () =>
      user ?? {
        firstName,
        lastName,
        fullName,
        avatarUrl
      },
    [user, firstName, lastName, fullName, avatarUrl]
  );

  const imageUrl = userObj.avatarUrl || avatarUrl;

  const shouldShowImage = useMemo(() => {
    if (forceInitials) return false;
    if (forceImage) return true;

    return globalShowImages && !!imageUrl;
  }, [forceInitials, forceImage, globalShowImages, imageUrl]);

  const initials = useMemo(() => getUserInitials(userObj), [userObj]);
  const avatarColor = useMemo(() => getUserAvatarColor(userObj), [userObj]);
  const altText = alt || [userObj.firstName, userObj.lastName].filter(Boolean).join(' ') || userObj.fullName || '';

  const sizeStyles = useMemo(() => {
    if (typeof size === 'number') {
      return {
        width: size,
        height: size,
        fontSize: `${Math.max(size * 0.4, 10)}px`
      };
    }

    return SIZE_MAP[size] || SIZE_MAP.medium;
  }, [size]);

  const avatarVariant = variant ?? (typeof size === 'string' ? SIZE_TO_VARIANT[size] : undefined);

  const combinedSx = useMemo(
    () =>
      mergeSx(
        !avatarVariant && sizeStyles,
        !shouldShowImage && {
          backgroundColor: avatarColor,
          color: 'background.default'
        },
        sx
      ),
    [avatarVariant, sizeStyles, shouldShowImage, avatarColor, sx]
  );

  return (
    <Avatar variant={avatarVariant} alt={altText} src={shouldShowImage ? imageUrl : undefined} sx={combinedSx}>
      {initials}
    </Avatar>
  );
};
